import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { isMarketingHost } from "@/lib/marketingHost";
import { CookieBanner } from "@/components/CookieBanner";

import { AuthProvider, useAuth } from "@/lib/auth";
import { useUserRole } from "@/hooks/useUserRole";
import { Layout } from "@/components/Layout";
import { ScrollToTop } from "@/components/ScrollToTop";
import ExpressionCheck from "@/pages/ExpressionCheck";
import { Loader2 } from "lucide-react";
import { ActiveCallProvider } from "@/lib/activeCall";
import { GlobalVideoCallOverlay } from "@/components/canvas/GlobalVideoCallOverlay";

// Keep install prompt lazy, but load update logic eagerly so service-worker
// checks start immediately on app boot.
const PWAInstallPrompt = lazy(() => import("@/components/PWAInstallPrompt").then(m => ({ default: m.PWAInstallPrompt })));
import { PWAUpdatePrompt } from "@/components/PWAUpdatePrompt";

// Eagerly load Auth page (needed immediately for login)
import Auth from "./pages/Auth";
import AuthCallback from "./pages/AuthCallback";

// Lazy load all other pages for code splitting
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Tasks = lazy(() => import("./pages/Tasks"));
const Vocabulary = lazy(() => import("./pages/Vocabulary"));
const Goals = lazy(() => import("./pages/Goals"));
const Audio = lazy(() => import("./pages/Audio"));
const Sessions = lazy(() => import("./pages/Sessions"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Profile = lazy(() => import("./pages/Profile"));
const VocabularyPractice = lazy(() => import("./pages/VocabularyPractice"));
const VocabularyQuiz = lazy(() => import("./pages/VocabularyQuiz"));
const VocabularyTopicPractice = lazy(() => import("./pages/VocabularyTopicPractice"));
const Admin = lazy(() => import("./pages/Admin"));
const ClientWorkspace = lazy(() => import("./pages/ClientWorkspace"));
const CoachingAssistant = lazy(() => import("./pages/CoachingAssistant"));
const ActivityLibrary = lazy(() => import("./pages/ActivityLibrary"));
const Help = lazy(() => import("./pages/Help"));
const GroupBooking = lazy(() => import("./pages/GroupBooking"));
const Announcements = lazy(() => import("./pages/Announcements"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const WebsitePrivacy = lazy(() => import("./pages/WebsitePrivacy"));
const WebsiteTerms = lazy(() => import("./pages/WebsiteTerms"));
const Impressum = lazy(() => import("./pages/Impressum"));
const PracticeHub = lazy(() => import("./pages/PracticeHub"));
const Practice = lazy(() => import("./pages/Practice"));
const CoachPractice = lazy(() => import("./pages/CoachPractice"));
const HowIPractice = lazy(() => import("./pages/HowIPractice"));
const GrowthPage = lazy(() => import("./pages/Growth"));
const Writing = lazy(() => import("./pages/Writing"));
const LiveCoachingCanvas = lazy(() => import("./pages/LiveCoachingCanvas"));
const ClientOnboarding = lazy(() => import("./pages/ClientOnboarding"));
const Intake = lazy(() => import("./pages/Intake"));
const Quiz = lazy(() => import("./pages/Quiz"));
const QuizResult = lazy(() => import("./pages/QuizResult"));
const Welcome = lazy(() => import("./pages/Welcome"));
const BookClub = lazy(() => import("./pages/BookClub"));
const SpotlightMagazine = lazy(() => import("./pages/SpotlightMagazine"));
const Start = lazy(() => import("./pages/Start"));
const LanguageLab = lazy(() => import("./pages/LanguageLab"));
const LanguageLabDocument = lazy(() => import("./pages/LanguageLabDocument"));
const SummerMomentum = lazy(() => import("./pages/SummerMomentum"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const Podcast = lazy(() => import("./pages/Podcast"));
const Sparks = lazy(() => import("./pages/Sparks"));
const CoachTodoPage = lazy(() => import("./pages/CoachTodoPage"));
const CoachingInsights = lazy(() => import("./pages/CoachingInsights"));
const CoachInsights = lazy(() => import("./pages/CoachInsights"));
const ExpressionProfiles = lazy(() => import("./pages/ExpressionProfiles"));
const VoiceLanding = lazy(() => import("./pages/VoiceLanding"));

const queryClient = new QueryClient();


// Loading fallback component
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return <Layout>{children}</Layout>;
}

// Route protection for Conversation Partners - redirects restricted routes
function ConversationPartnerBlockedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const { isConversationPartner, loading: roleLoading } = useUserRole();
  
  if (loading || roleLoading) {
    return <PageLoader />;
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  // Block conversation partners from accessing these routes
  if (isConversationPartner) {
    return <Navigate to="/" replace />;
  }
  
  return <Layout>{children}</Layout>;
}

function PracticeRouter() {
  const { isCoach } = useUserRole();
  return isCoach ? <CoachPractice /> : <Practice />;
}

function WritingRouter() {
  const { isCoach } = useUserRole();
  if (isCoach) {
    return <Navigate to="/practice?tab=writing" replace />;
  }
  return <Writing />;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <PWAUpdatePrompt />
        <BrowserRouter>
          <Suspense fallback={null}>
            <PWAInstallPrompt />
            <CookieBanner />
          </Suspense>
          <ActiveCallProvider>
            <ScrollToTop />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/auth" element={<Auth />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/datenschutz" element={<WebsitePrivacy />} />
                <Route path="/agb" element={<WebsiteTerms />} />
                <Route path="/impressum" element={<Impressum />} />
                <Route path="/expression-quiz" element={<Quiz />} />
                <Route path="/de/expression-quiz" element={<Quiz routeLang="de" />} />
                <Route path="/expression-quiz/result" element={<QuizResult />} />
                <Route path="/start" element={isMarketingHost() ? <Navigate to="/" replace /> : <Start />} />
                <Route path="/de/start" element={isMarketingHost() ? <Navigate to="/de" replace /> : <Start routeLang="de" />} />
                <Route path="/de" element={<Start routeLang="de" />} />
                <Route path="/pure-practice" element={<Start page="program" />} />
                <Route path="/de/pure-practice" element={<Start routeLang="de" page="program" />} />
                <Route path="/expression-check" element={<ExpressionCheck lang="en" />} />
                <Route path="/de/expression-check" element={<ExpressionCheck lang="de" />} />
                <Route path="/client-stories" element={<Start page="stories" />} />
                <Route path="/de/client-stories" element={<Start routeLang="de" page="stories" />} />
                <Route path="/your-expression-gap" element={<ExpressionProfiles />} />
                <Route path="/de/your-expression-gap" element={<ExpressionProfiles routeLang="de" />} />
                <Route path="/everyday-voice" element={<VoiceLanding voice="everyday" />} />
                <Route path="/de/everyday-voice" element={<VoiceLanding routeLang="de" voice="everyday" />} />
                <Route path="/global-voice" element={<VoiceLanding voice="global" />} />
                <Route path="/de/global-voice" element={<VoiceLanding routeLang="de" voice="global" />} />
                <Route path="/professional-voice" element={<VoiceLanding voice="professional" />} />
                <Route path="/de/professional-voice" element={<VoiceLanding routeLang="de" voice="professional" />} />
                <Route path="/your-expression-gap/speak-with-precision" element={<ExpressionProfiles profile="precision" />} />
                <Route path="/de/your-expression-gap/speak-with-precision" element={<ExpressionProfiles routeLang="de" profile="precision" />} />
                <Route path="/your-expression-gap/speak-with-confidence" element={<ExpressionProfiles profile="confidence" />} />
                <Route path="/de/your-expression-gap/speak-with-confidence" element={<ExpressionProfiles routeLang="de" profile="confidence" />} />
                <Route path="/summer-momentum" element={<SummerMomentum />} />
                <Route path="/welcome" element={<Welcome />} />
                <Route path="/de/welcome" element={<Welcome routeLang="de" />} />
                <Route path="/podcast" element={<Podcast />} />
                <Route path="/coaching-insights" element={<CoachingInsights />} />
                <Route path="/coaching-insights/:slug" element={<CoachingInsights />} />
                <Route path="/de/coaching-insights" element={<CoachingInsights routeLang="de" />} />
                <Route path="/de/coaching-insights/:slug" element={<CoachingInsights routeLang="de" />} />
                <Route path="/coach-insights" element={<ProtectedRoute><CoachInsights /></ProtectedRoute>} />
                <Route path="/quiz" element={<Navigate to="/expression-quiz" replace />} />
                <Route path="/quiz/result" element={<Navigate to="/expression-quiz/result" replace />} />
                <Route path="/intake/:token" element={<Intake />} />
                <Route path="/" element={isMarketingHost() ? <Start /> : <ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/tasks" element={<ConversationPartnerBlockedRoute><Tasks /></ConversationPartnerBlockedRoute>} />
                <Route path="/vocabulary" element={<ConversationPartnerBlockedRoute><Vocabulary /></ConversationPartnerBlockedRoute>} />
                <Route path="/vocabulary/practice" element={<ConversationPartnerBlockedRoute><VocabularyPractice /></ConversationPartnerBlockedRoute>} />
                <Route path="/vocabulary/quiz" element={<ConversationPartnerBlockedRoute><VocabularyQuiz /></ConversationPartnerBlockedRoute>} />
                <Route path="/vocabulary/topic-practice" element={<ConversationPartnerBlockedRoute><VocabularyTopicPractice /></ConversationPartnerBlockedRoute>} />
                <Route path="/audio" element={<ConversationPartnerBlockedRoute><Audio /></ConversationPartnerBlockedRoute>} />
                <Route path="/sessions" element={<ProtectedRoute><Sessions /></ProtectedRoute>} />
                <Route path="/goals" element={<ConversationPartnerBlockedRoute><Goals /></ConversationPartnerBlockedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/admin" element={<ConversationPartnerBlockedRoute><Admin /></ConversationPartnerBlockedRoute>} />
                <Route path="/client/:id" element={<ConversationPartnerBlockedRoute><ClientWorkspace /></ConversationPartnerBlockedRoute>} />
                <Route path="/activity-library" element={<ConversationPartnerBlockedRoute><ActivityLibrary /></ConversationPartnerBlockedRoute>} />
                <Route path="/coaching-assistant" element={<ConversationPartnerBlockedRoute><CoachingAssistant /></ConversationPartnerBlockedRoute>} />
                <Route path="/group-booking" element={<ConversationPartnerBlockedRoute><GroupBooking /></ConversationPartnerBlockedRoute>} />
                <Route path="/announcements" element={<ConversationPartnerBlockedRoute><Announcements /></ConversationPartnerBlockedRoute>} />
                <Route path="/practice-hub" element={<ConversationPartnerBlockedRoute><PracticeHub /></ConversationPartnerBlockedRoute>} />
                <Route path="/sparks" element={<ConversationPartnerBlockedRoute><Sparks /></ConversationPartnerBlockedRoute>} />
                <Route path="/coach-todo" element={<ConversationPartnerBlockedRoute><CoachTodoPage /></ConversationPartnerBlockedRoute>} />
                <Route path="/book-club" element={<ConversationPartnerBlockedRoute><BookClub /></ConversationPartnerBlockedRoute>} />
                <Route path="/spotlight-magazine" element={<ConversationPartnerBlockedRoute><SpotlightMagazine /></ConversationPartnerBlockedRoute>} />
                <Route path="/language-lab" element={<ConversationPartnerBlockedRoute><LanguageLab /></ConversationPartnerBlockedRoute>} />
                <Route path="/language-lab/:id" element={<ConversationPartnerBlockedRoute><LanguageLabDocument /></ConversationPartnerBlockedRoute>} />
                <Route path="/practice" element={<ConversationPartnerBlockedRoute><PracticeRouter /></ConversationPartnerBlockedRoute>} />
                <Route path="/how-i-practice" element={<ConversationPartnerBlockedRoute><HowIPractice /></ConversationPartnerBlockedRoute>} />
                <Route path="/writing" element={<ConversationPartnerBlockedRoute><WritingRouter /></ConversationPartnerBlockedRoute>} />
                <Route path="/growth" element={<ConversationPartnerBlockedRoute><GrowthPage /></ConversationPartnerBlockedRoute>} />
                <Route path="/onboarding/:clientId" element={<ConversationPartnerBlockedRoute><ClientOnboarding /></ConversationPartnerBlockedRoute>} />
                <Route path="/intake" element={<ProtectedRoute><Intake /></ProtectedRoute>} />
                <Route path="/sessions/:sessionId/canvas" element={<ProtectedRoute><LiveCoachingCanvas /></ProtectedRoute>} />
                <Route path="/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            <GlobalVideoCallOverlay />
          </ActiveCallProvider>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
